import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {decodeJQueryParam, parseUri} from 'BenchlingUtil';
import {ActionMethod, ActionOrigin, FORMS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import SharedFormDefinitionPage, {
  getFormDefinitionActionOrigin,
} from 'forms/form-definition/SharedFormDefinitionPage';
import {FormDefinitionContent, FormActionConfigInput} from 'forms/form-designer/Constants';
import {CREATE_TASK_ACTION} from 'forms/form-designer/FormDesigner';
import {createForm, formDefinitionUrl} from 'forms/form-designer/FormDesignerDAO';
import {InitialFormDesignerAction, initializeFormDesignerState} from 'forms/form-designer/FormDesignerState';
import {FormDesignerProcessTaskSchemasQuery} from 'forms/form-designer/graphql/__generated/FormDesignerProcessTaskSchemasQuery';
import {
  InitializeFormDesignerWithFormActionQuery,
  InitializeFormDesignerWithFormActionQueryVariables,
} from 'forms/form-designer/graphql/__generated/InitializeFormDesignerWithFormActionQuery';
import {
  GET_ALL_PROCESS_TASK_SCHEMAS,
  INITIALIZE_FORM_DESIGNER_WITH_FORM_ACTION_QUERY,
} from 'forms/form-designer/graphql/FormDesignerQueries';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL, {clearNavigationWarnings} from 'lib/navigateToURL';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

type InitialProcessTaskSchema =
  InitializeFormDesignerWithFormActionQuery['processTaskSchemasSupportingPlatformFeature'][number];

export default function CreateFormDefinitionPage(): JSX.Element {
  const [sessionId] = useState(analytics.makeAnalyticsEventId());

  // Parse initial options from URL parameters
  const {queryKey} = parseUri(window.location.href);
  const taskSchemaId: string | undefined = queryKey.taskSchemaId;
  const initialName = decodeJQueryParam(queryKey.name || '');

  // Analytics event only gets called once since dependencies are only on URL parameters and sessionid.
  useEffect(() => {
    analytics.trackEvent(FORMS_ANALYTICS_ACTIONS.OPENED_FORM_DESIGNER, {
      session_id: sessionId,
      action_method: ActionMethod.CREATE_FORM_DEFINITION,
      action_origin: getFormDefinitionActionOrigin(),
      name: initialName,
      template_collection_id: queryKey.templateCollection,
      task_schema_id: taskSchemaId,
    });
  }, [queryKey, sessionId, initialName, taskSchemaId]);

  const [name, setName] = useState<string>(initialName);

  const [templateCollectionId, _setTemplateCollectionId] = useState(queryKey.templateCollection || '');
  // Update sidebar with collection name on update
  function setTemplateCollectionId(newTemplateCollectionId: string): void {
    TemplateActions.activateCollection(newTemplateCollectionId);
    _setTemplateCollectionId(newTemplateCollectionId);
  }

  // Create form and go to template collection page with success toast.
  // Returns whether form creation was successful.
  const onCreate = async (
    formDefinition: FormDefinitionContent,
    formActionConfigs: Array<FormActionConfigInput>
  ): Promise<boolean> => {
    if (!APIUtil.isTemplateCollection(templateCollectionId)) {
      notifications.error(
        'Invalid template collection. Please choose a template collection that you have write access to.'
      );
      return false;
    }
    const taskSchemaId = formActionConfigs[0].schemaId!;
    const {formDefinitionId} = await withLoadingScreen(
      createForm(name, templateCollectionId, formDefinition, formActionConfigs, taskSchemaId),
      'Creating form...'
    );
    clearNavigationWarnings();
    // Go to edit form definition page and show success message.
    notifications.success(`Created form "${name}"`);
    navigateToURL(formDefinitionUrl(formDefinitionId), {
      replace: true,
    });
    return true;
  };

  // if we are provided with a Task Schema, then we also query for the initial form action. If not, we only fetch
  // the Task Schemas.
  const formActionResult = useSkippableBenchlingQuery<
    InitializeFormDesignerWithFormActionQuery,
    InitializeFormDesignerWithFormActionQueryVariables
  >(INITIALIZE_FORM_DESIGNER_WITH_FORM_ACTION_QUERY, {
    variables: {actionIdentifier: CREATE_TASK_ACTION.identifier, schemaId: taskSchemaId},
    fetchPolicy: 'network-only',
    skip: !taskSchemaId,
  });
  const processTaskSchemasResult = useSkippableBenchlingQuery<FormDesignerProcessTaskSchemasQuery>(
    GET_ALL_PROCESS_TASK_SCHEMAS,
    {
      fetchPolicy: 'cache-and-network',
      skip: Boolean(taskSchemaId),
    }
  );
  const result = taskSchemaId ? formActionResult : processTaskSchemasResult;
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  if (result.type === QueryResultType.ERROR || !result.data) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load Form Designer" />;
  }
  const formAction = taskSchemaId
    ? (result.data as InitializeFormDesignerWithFormActionQuery).formAction
    : undefined;
  const processTaskSchemas = result.data.processTaskSchemasSupportingPlatformFeature;

  const action: InitialFormDesignerAction = {
    action: CREATE_TASK_ACTION,
    lockAction: true,
  };

  if (taskSchemaId) {
    const initialSchema = processTaskSchemas.find((schema) => schema.id === taskSchemaId);
    if (initialSchema) {
      action.schema = initialSchema as InitialProcessTaskSchema;
      action.lockSchema = true;
    } else {
      notifications.error(`Could not find schema ${taskSchemaId}`);
    }
  }

  if (formAction && !isInaccessible(formAction)) {
    action.formAction = formAction;
  }

  const formDesignerState = initializeFormDesignerState({
    initialActions: [action],
  });

  const actionToSchemas = {
    [CREATE_TASK_ACTION.identifier]: processTaskSchemas as Array<InitialProcessTaskSchema>,
  };

  return (
    <SharedFormDefinitionPage
      name={name}
      setName={setName}
      templateCollectionId={templateCollectionId}
      setTemplateCollectionId={setTemplateCollectionId}
      onSave={onCreate}
      saveButtonText="Create"
      initialFormDesignerState={formDesignerState}
      actionToSchemas={actionToSchemas}
      sessionId={sessionId}
      actionOrigin={ActionOrigin.FORMS_CREATE_FORM_DEFINITION_DESIGNER}
    />
  );
}
